import { Briefcase, ChevronDown, MessageSquare, Settings, Zap } from 'lucide-react';
import { useState, type ComponentType, type SVGProps } from 'react';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { TeamsDropdown } from '../../common/components/TeamsDropdown.js';
import { cn } from '../../../lib/utils.js';
import { FusionRequestSheet } from './FusionRequestSheet.js';

/**
 * Top toolbar above the scenario list. Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenarioHeader/Provider.tsx`
 * + surrounding `HeaderProvider`:
 *
 *   [ Scenarios  | All scenarios ▾ ]   [ Business · Admin · Developer ]   [ Fusion Request ]
 *      ↑ title + folder filter           ↑ centered role pills              ↑ right-aligned
 *
 * `All scenarios ▾` is a folder picker: choices are derived from the
 * `folderName` field of the engine response (deduplicated). Selecting
 * a named folder narrows the list to scenarios in that folder;
 * `Uncategorized` keeps rows with no folder; `All scenarios` clears
 * the filter.
 */
interface RolePill {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
}

const ROLES: ReadonlyArray<RolePill> = [
  { to: '/scenarios', label: 'Business Executive', icon: Briefcase, end: true },
  { to: '/scenarios/administrator', label: 'Workfront Administrator', icon: Settings },
  { to: '/scenarios/developer', label: 'Fusion Developer', icon: Zap },
];

export const FOLDER_ALL = '__all__';
export const FOLDER_UNCATEGORIZED = '__uncategorized__';
export const TEAM_ALL = '__all__';

export interface ScenarioTeamOption {
  fusionId: string;
  name: string;
}

export interface ScenarioPageHeaderProps {
  folder: string;
  onFolderChange: (folder: string) => void;
  /** Sorted, deduplicated folder names present in the current license's scenarios. */
  folderOptions: ReadonlyArray<string>;
  /** Currently-selected team (fusion id) or `TEAM_ALL`. */
  team: string;
  onTeamChange: (team: string) => void;
}

export function ScenarioPageHeader({
  folder,
  onFolderChange,
  folderOptions,
  team,
  onTeamChange,
}: ScenarioPageHeaderProps): JSX.Element {
  // Top-right "Fusion Request" button: standalone request (no scenario
  // context). Mirrors legacy `wf-audit/.../header/Provider.tsx` which
  // renders `<FusionRequestProvider showLabel={true} />` here — the
  // ungated entry-point operators use when they want to file a request
  // not tied to a specific scenario row. Distinct from the comment
  // icon in `ScenarioActionsCell` which seeds the subject from the
  // row's scenario name.
  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);

  const folderLabel =
    folder === FOLDER_ALL
      ? 'All scenarios'
      : folder === FOLDER_UNCATEGORIZED
        ? 'Uncategorized'
        : folder;

  return (
    <header className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 pb-4 pt-6">
      {/* Left: title + folder dropdown. Teams used to live here in
          PR #97 — operator-confirmed legacy parity move (`sec_038`,
          `sec_090`) puts Teams in the top-right corner alongside
          Fusion Request, so the title stays "Scenarios | <folder>". */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-light text-foreground">Scenarios</h1>
        <span className="text-base text-foreground/40">|</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-base text-foreground/80 transition-colors hover:border-foreground/15 hover:bg-foreground/5 hover:text-foreground"
            >
              <span>{folderLabel}</span>
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
            <DropdownMenuLabel>Folder</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_ALL}
              onCheckedChange={() => onFolderChange(FOLDER_ALL)}
            >
              All scenarios
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_UNCATEGORIZED}
              onCheckedChange={() => onFolderChange(FOLDER_UNCATEGORIZED)}
            >
              Uncategorized
            </DropdownMenuCheckboxItem>
            {folderOptions.length > 0 ? <DropdownMenuSeparator /> : null}
            {folderOptions.map((name) => (
              <DropdownMenuCheckboxItem
                key={name}
                checked={folder === name}
                onCheckedChange={() => onFolderChange(name)}
              >
                {name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Center: role pills */}
      <nav className="flex items-center gap-2">
        {ROLES.map((r) => (
          <NavLink
            key={r.to}
            to={r.to}
            end={r.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                isActive
                  ? 'border-[color:var(--brand-yellow)] bg-foreground/5 text-foreground'
                  : 'border-foreground/15 bg-transparent text-foreground/70 hover:border-foreground/40 hover:text-foreground',
              )
            }
          >
            <r.icon className="h-4 w-4" />
            <span>{r.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Right: Teams selector + Fusion Request. Teams moved here
          from the inline title-bar group per legacy parity. */}
      <div className="flex items-center justify-end gap-3">
        <TeamsDropdown team={team} onTeamChange={onTeamChange} />
        <button
          type="button"
          onClick={() => setFusionRequestOpen(true)}
          aria-label="Make a Fusion Enhancement Request"
          className="flex items-center gap-2 rounded-md border border-foreground/15 px-3 py-1.5 text-sm text-foreground/70 transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          <MessageSquare className="h-4 w-4" />
          <span>Fusion Request</span>
        </button>
      </div>
      <FusionRequestSheet open={fusionRequestOpen} onOpenChange={setFusionRequestOpen} />
    </header>
  );
}
